import { Component, computed, Inject, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClose, faCircleInfo, faFileSpreadsheet, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { EiqFilesModule } from '@eiq/files';
import { UiAdminModule } from '@eiq/ui';

import { FileType, FileTypeExtension } from '@empathy/global/data';

import { ToPlural } from '@empathy/global/utils';
import { SURVEY_DEFAULT_LANGUAGE_CODE, Translation } from '@empathy/survey-core';

import { TranslationsImportIssueType } from '../../../models/translations/translations-import-issue-type.enum';
import { TranslationsImportResult } from '../../../models/translations/translations-import-result.interface';
import { ImportTranslationsService } from '../../../services/import-translations.service';

@Component({
  standalone: true,
  templateUrl: 'import-translations-dialog.component.html',
  styleUrls: ['./import-translations-dialog.component.scss'],
  imports: [FontAwesomeModule, EiqFilesModule, FormsModule, MatCheckbox, MatDialogModule, UiAdminModule]
})
export class ImportTranslationsDialogComponent {
  uploading$ = signal(false);
  uploadedFile$ = signal<File | null>(null);
  importResult$ = signal<TranslationsImportResult>(null);
  overwriteExisting$ = model(false);

  canImport$ = computed(() => this.canImport());
  issuesMessage$ = computed(() => this.getIssuesMessage());
  fileSize$ = computed(() => this.getFileSize());

  acceptedTypes = [FileType.ExcelXlsx];
  acceptedTypesText = [FileTypeExtension.Xlsx];
  maxFileSize = 100;

  defaultLanguageCode$ = inject(SURVEY_DEFAULT_LANGUAGE_CODE);

  faClose = faClose;
  faCircleInfo = faCircleInfo;
  faFileSpreadsheet = faFileSpreadsheet;
  faTrash = faTrash;

  importIssueType = TranslationsImportIssueType;

  private importTranslationsService = inject(ImportTranslationsService);

  constructor(
    private dialogRef: MatDialogRef<ImportTranslationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { translation: Translation }
  ) {}

  onUpload(file: File): void {
    this.uploadedFile$.set(file);
    this.importResult$.set(null);
  }

  onRemoveFile(): void {
    this.uploadedFile$.set(null);
    this.importResult$.set(null);
  }

  async onImport(): Promise<void> {
    const file = this.uploadedFile$();
    if (!file) {
      return;
    }

    const currentResult = this.importResult$();

    if (currentResult) {
      const hasBlockingIssues = currentResult.issues.some((e) => e.type === TranslationsImportIssueType.Error);

      if (!hasBlockingIssues) {
        this.dialogRef.close(currentResult);
      }

      return;
    }

    const result = await this.importTranslationsService.import(file, this.data.translation, this.overwriteExisting$());

    this.importResult$.set(result);

    const hasAnyIssues = result.issues.length > 0;

    if (!hasAnyIssues) {
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private canImport(): boolean {
    const file = this.uploadedFile$();
    const result = this.importResult$();

    if (!file) {
      return false;
    }

    if (!result) {
      return true;
    }

    return !result.issues.some((e) => e.type === TranslationsImportIssueType.Error);
  }

  private getIssuesMessage(): string | null {
    const result = this.importResult$();

    if (!result || !result.issues.length) {
      return null;
    }

    const warnings = result.issues.filter((e) => e.type === TranslationsImportIssueType.Warning);
    const errors = result.issues.filter((e) => e.type === TranslationsImportIssueType.Error);

    const parts: string[] = [];

    if (warnings.length > 0) {
      parts.push(ToPlural('warning', warnings));
    }

    if (errors.length > 0) {
      parts.push(ToPlural('error', errors));
    }

    return `We found ${parts.join(' and ')} in your file.`;
  }

  private getFileSize(): string {
    const file = this.uploadedFile$();

    if (!file) {
      return '';
    }

    const kb = file.size / 1024;
    const mb = kb / 1024;

    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(0)} KB`;
  }
}
